import { IconEditLine, Text } from "@instructure/ui";
import { useTranslation } from "react-i18next";
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import DataGrid from "react-data-grid";
import { MasteryLevelContext } from "../../../../utils/Context";
import { TruncateToolTip } from "../../../../utils/TruncateTooltip";
import RatingDescriptionModal from "./RatingDescriptionModal";

const RaterGridAdd = forwardRef(
  ({ outcome, masteryMapping, adding, rubricData, setRubricData }, ref) => {
    const { t } = useTranslation();
    const { masteryLevels } = useContext(MasteryLevelContext);
    const [rows, setRows] = useState([]);
    const [cols, setCols] = useState([]);
    const [openDescModal, setOpenDescModal] = useState(false);
    const [descData, setDescData] = useState({
      description: "",
      masteryLevel: "",
      row: {},
      column: {},
    });

    const findRatingScaleId = () => {
      if (outcome.rating_scales && outcome.rating_scales.length !== 0) {
        const scale = outcome.rating_scales.find(
          (s) => s.rating_to_mastery_level_map === masteryMapping.id
        );
        if (scale) {
          return scale.ddl_id;
        }
      } else {
        return "";
      }
    };

    useImperativeHandle(ref, () => ({ handleCascade: handleCascade }));

    useEffect(() => {
      if (setRubricData) {
        setRubricData({
          id: outcome.id,
          map_id: masteryMapping.id,
          levels: [],
          mastery_at: masteryMapping.mastery_points,
          rating_scale_id: findRatingScaleId(),
        });
      }
    }, [masteryMapping, outcome, setRubricData]);

    const renderContent = ({ row, column }) => {
      return (
        <div
          style={{
            lineHeight: 1,
            padding: "10px 2px",
            display: "block",
            position: "relative",
            textWrap: "wrap",
            textAlign: row.id === "raterRow" ? "center" : "left",
            height: 150,
            cursor: adding && row.id !== "raterRow" ? "pointer" : "default",
            fontWeight: row.id === "raterRow" && "bold",
          }}
        >
          <TruncateToolTip
            renderTip={
              <div style={{ maxWidth: 400 }}>
                {row[column.key] || t("ratingScale.descriptionPlaceholder")}
              </div>
            }
            maxLines={9}
          >
            <Text
              size="small"
              fontStyle={row[column.key] === "" ? "italic" : "normal"}
            >
              {row[column.key]
                ? row[column.key]
                : t("ratingScale.descriptionPlaceholder")}
            </Text>
          </TruncateToolTip>
          {adding && row.id !== "raterRow" && (
            <p style={{ position: "absolute", bottom: 10, right: 5 }}>
              <IconEditLine color="brand" />
            </p>
          )}
        </div>
      );
    };

    useEffect(() => {
      if (Object.keys(masteryMapping).length > 0) {
        const mapObj = [];
        const rowObj = [{ id: "raterRow" }];
        rowObj.push({ slo: outcome.name + ": " + outcome.statement });
        const levelCounter = {};
        const dataLevels = [];
        masteryMapping.levels.forEach((lev) => {
          if (levelCounter[lev.mastery_level_id] === undefined) {
            levelCounter[lev.mastery_level_id] = 1;
          } else {
            levelCounter[lev.mastery_level_id] += 1;
          }
        });
        masteryMapping.levels.forEach((lev) => {
          const ml = masteryLevels.find(
            (level) => level.id === lev.mastery_level_id
          );
          const obj = {
            key: lev.id,
            name: (
              <div style={{ textAlign: "center" }}>
                <TruncateToolTip
                  renderTip={<div style={{ maxWidth: 400 }}>{ml.level}</div>}
                >
                  <Text size="x-small">{ml.level}</Text>
                </TruncateToolTip>
              </div>
            ),
            id: lev.mastery_level_id,
            insights_rating_level: lev.id,
            score: parseInt(lev.points),
            colSpan(args) {
              if (args.type === "HEADER") {
                return levelCounter[lev.mastery_level_id];
              }
            },
            renderCell: renderContent,
          };
          mapObj.push(obj);
          rowObj.forEach((row, index) => {
            if (index === 0) {
              row[lev.id] = lev.points;
            } else {
              let levelId = undefined;
              if (
                outcome.rating_scales &&
                outcome.rating_scales.length !== 0 &&
                outcome.rating_scales.find(
                  (s) => s.rating_to_mastery_level_map === masteryMapping.id
                )
              ) {
                let rating = {};
                const scale = outcome.rating_scales.find(
                  (s) => s.rating_to_mastery_level_map === masteryMapping.id
                );
                scale.rating_levels.forEach((level) => {
                  if (
                    level.insights_rating_level &&
                    (level.insights_rating_level === lev.id ||
                      level.insights_rating_level.id === lev.id)
                  ) {
                    rating = level;
                    levelId = level.ddl_id;
                  }
                });
                row[lev.id] = rating
                  ? rating.description
                  : t("ratingScale.outcomeCorrupt");
              } else {
                row[lev.id] = "";
              }
              dataLevels.push({
                points: parseInt(lev.points),
                description: row[lev.id],
                insights_rating_level: lev.id,
                id: levelId,
              });
            }
          });
        });
        if (rubricData) {
          setRubricData((prevData) => ({ ...prevData, levels: dataLevels }));
        }
        mapObj.sort((a, b) => b.score - a.score);
        setCols(mapObj);
        setRows(rowObj);
      }
    }, [masteryMapping]);

    const handleCascade = () => {
      const newRows = JSON.parse(JSON.stringify(rows));
      const newRubricData = { ...rubricData };
      Object.keys(newRows[1]).forEach((loKey) => {
        const masteryLevel = masteryMapping.levels.find(
          (level) => level.id === loKey
        );
        if (masteryLevel) {
          newRows[1][loKey] = masteryLevel.description;
          const i = newRubricData.levels.findIndex(
            (lvl) => lvl.insights_rating_level === masteryLevel.id
          );
          newRubricData.levels[i].description = masteryLevel.description;
        }
      });
      setRows(newRows);
      setRubricData(newRubricData);
    };

    const handleCellClick = (args) => {
      const row = args.row;
      const column = args.column;
      if (adding && row.id !== "raterRow") {
        setOpenDescModal(true);
        setDescData({
          description: row[column.key],
          masteryLevel:
            column.name.props.children.props.children.props.children,
          row: row,
          column: column,
        });
      }
    };

    const handleCellKeyDown = (args, e) => {
      if (e.key === "Enter") {
        handleCellClick(args);
      }
    };

    const handleSaveDesc = (newDesc) => {
      const newRows = JSON.parse(JSON.stringify(rows));
      const rowIndex = newRows.findIndex((r) => r.id === descData.row.id);
      newRows[rowIndex][descData.column.key] = newDesc;
      setRows(newRows);
      setOpenDescModal(false);

      const i = rubricData.levels.findIndex(
        (lvl) =>
          descData.column.insights_rating_level === lvl.insights_rating_level
      );
      const newRubricData = { ...rubricData };
      newRubricData.levels[i].description = newDesc;
      setRubricData(newRubricData);
    };

    const rowHeight = (row) => {
      if (row.id === "raterRow") {
        return 35;
      } else {
        return 150;
      }
    };

    return (
      cols.length > 0 && (
        <>
          <DataGrid
            key={cols.length}
            className="rdg-light"
            style={{ height: "auto" }}
            columns={cols}
            rows={rows}
            rowHeight={rowHeight}
            headerRowHeight={40}
            onCellClick={handleCellClick}
            onCellKeyDown={handleCellKeyDown}
            defaultColumnOptions={{ width: "minmax(100px, auto)" }}
          />
          <RatingDescriptionModal
            openModal={openDescModal}
            setOpenModal={setOpenDescModal}
            descData={descData}
            handleSaveDesc={handleSaveDesc}
          />
        </>
      )
    );
  }
);

export default RaterGridAdd;
